import React from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import emitter from "tiny-emitter/instance";
import * as moment from "moment";
import { connect } from 'react-redux';
import store from "./redux/store";
import { getUserSandbox } from "./redux/slices/sandbox";
import { fetchUserClients } from "./redux/slices/userClients";

import "./index.css";

import Layout from "./Layout";
import apiData from "./services/api_data";
import AuthService from "./services/auth.service";
import { Toaster } from "react-hot-toast";

/**
 * Resolves filter arrays that may contain indices (numbers) to API-expected string names.
 * Ensures the same payload shape from all environments (Windows vs server) so the backend always receives names.
 * Ensures all list fields are arrays so the API receives a consistent payload.
 */
function resolveFilterIndicesToNames(filters, data) {
	if (!filters) return filters;
	const res = { ...filters };
	const toName = (v, lookup, useItemName) => {
		if (typeof v === "string") return v;
		if (typeof v === "number" && lookup && lookup[v] != null) {
			const item = lookup[v];
			return (useItemName && item && typeof item === "object" && item.itemName != null) ? item.itemName : item;
		}
		return null;
	};
	const resolve = (arr, lookup, useItemName) => {
		if (!Array.isArray(arr)) return [];
		return arr.map((v) => toName(v, lookup, useItemName)).filter((v) => v != null && v !== "");
	};
	const ensureArray = (v) => (Array.isArray(v) ? v : []);

	res.mediaType = resolve(ensureArray(res.mediaType), data?.mediaTypes, false);
	res.brand = resolve(ensureArray(res.brand), data?.brands, false);
	res.mediaSource = resolve(ensureArray(res.mediaSource), data?.mediaSources, true);
	res.category = resolve(ensureArray(res.category), data?.categories, false);
	res.subCategory = resolve(ensureArray(res.subCategory), data?.subcategories, false);
	res.type = resolve(ensureArray(res.type), data?.types, false);
	res.sentiment = resolve(ensureArray(res.sentiment), data?.sentiment, true);
	res.author = resolve(ensureArray(res.author), data?.authors, true);
	res.topic = resolve(ensureArray(res.topic), data?.topics, false);
	res.advertType = resolve(ensureArray(res.advertType), data?.advertTypes, true);
	res.language = resolve(ensureArray(res.language), data?.languages, true);
	res.country = resolve(ensureArray(res.country), data?.countries, false);
	res.location = resolve(ensureArray(res.location), data?.locations, true);
	res.exclude = ensureArray(res.exclude);
	res.search = typeof res.search === "string" ? res.search : "";
	return res;
}

const Login = React.lazy(() => import("./screens/Login"));
const Dashboard = React.lazy(() => import("./screens/Dashboard"));
const Volume = React.lazy(() => import("./screens/Volume"));
const MediaType = React.lazy(() => import("./screens/MediaType"));
const Source = React.lazy(() => import("./screens/Source"));
const Category = React.lazy(() => import("./screens/Category"));
const Language = React.lazy(() => import("./screens/Language"));
const LSMSEM = React.lazy(() => import("./screens/LSMSEM"));
const Value = React.lazy(() => import("./screens/Value"));
const Brand = React.lazy(() => import("./screens/Brand"));
const Topic = React.lazy(() => import("./screens/Topic"));
const AdvertType = React.lazy(() => import("./screens/AdvertType"));
const TopAuthors = React.lazy(() => import("./screens/TopAuthors"));
const AlertHistory = React.lazy(() => import("./screens/AlertHistory"));
const FAQ = React.lazy(() => import("./screens/FAQ"));
const Sentiment = React.lazy(() => import("./screens/Sentiment"));
const Data = React.lazy(() => import("./screens/Data"));
const Sandbox = React.lazy(() => import("./screens/Sandbox"));
const Region = React.lazy(() => import("./screens/Region"));
const WordCloud = React.lazy(() => import("./screens/WordCloud"));
const UserManagement = React.lazy(() => import("./screens/UserManagement"));
const ClientManagement = React.lazy(() => import("./screens/ClientManagement"));
const SocialMediaEngagement = React.lazy(() =>
	import("./screens/SocialMediaEngagement")
);
const Newsletters = React.lazy(() => import("./screens/Newsletters"));
const NewsletterEditor = React.lazy(() => import("./screens/Newsletters/NewsletterEditor"));
const PublicNewsletter = React.lazy(() => import("./screens/PublicNewsletter"));
const Ebooks = React.lazy(() => import("./screens/Ebooks"));
const EbookEditor = React.lazy(() => import("./screens/Ebooks/EbookEditor"));
const EbookView = React.lazy(() => import("./screens/EbookView"));
// const Alerts = React.lazy(() => import("./screens/Alert"));

class AppRouter extends React.Component {
	constructor(props) {
		super();
		this.state = {
			auth: null,
			data: null,
		};
		this.selectedClient = null;
		this.initialData = null;
	}

	componentDidMount() {
		this.initDate();
		this.initFilters();
		this.initPressedMediaTypes();

		// Restore auth from storage so direct navigation (e.g. /ebook/view/1 in new tab) works
		if (this.state.auth === null) {
			const user = AuthService.getCurrentUser();
			if (user && user.logged) {
				this.setState({ auth: user }, () => this.props.getUserSandbox());
				this.getData();
			} else {
				this.setState({ auth: { logged: false } });
			}
		}

		emitter.on("login", () => {
			const user = AuthService.getCurrentUser();
			if (user.logged)
			{
				this.setState({ auth: user }, () => this.props.getUserSandbox());
				this.getData();
			}
		});

		emitter.on("logout", (user) => {
			AuthService.logout();
		});

		emitter.on("dateRange", (range) => {
			let newRange = range;
			// Handle both Date objects and timestamps
			const startDateObj = newRange[0].startDate instanceof Date 
				? newRange[0].startDate 
				: new Date(newRange[0].startDate);
			const endDateObj = newRange[0].endDate instanceof Date 
				? newRange[0].endDate 
				: new Date(newRange[0].endDate);
			let startDate = moment(startDateObj).format("YYYY-MM-DD");
			let endDate = moment(endDateObj).format("YYYY-MM-DD");

			this.setState(
				{
					data: {
						...this.state.data,
						dateRange: {
							...this.state.dateRange,
							currentDataDateFrom: startDate,
							currentDataDateTo: endDate,
						},
					},
				},
				() => {
					sessionStorage.setItem(
						"dateRange",
						JSON.stringify({
							currentDataDateFrom: startDate,
							currentDataDateTo: endDate,
						})
					);
					this.getData(1);
				}
			);
		});

		emitter.on("getData", () => {
			this.getData();
		});

		emitter.on("filterChange", () => {
			// console.log('DATA INAINTE: ', data);
			this.setState(
				{
					data: {
						...this.state.data,
					},
				},
				() => { }
			);
		});

		emitter.on("restoreAnalytics", () => {
			const currentFilters = JSON.parse(
				sessionStorage.getItem("filters")
			);
			currentFilters.exclude = [];
			sessionStorage.setItem("filters", JSON.stringify(currentFilters));

			this.getData();
		});

		emitter.on("filter", () => {
			// Callers (FormFilters, LegendComponent, etc.) already set sessionStorage "filters".
			// Refetch dashboard/charts/grid so all components reflect the new filters.
			this.getData();
		});
	}

	componentWillUnmount() {
		emitter.off("login");
		emitter.off("logout");
		emitter.off("dateRange");
		emitter.off("getData");
		emitter.off("filterChange");
		emitter.off("restoreAnalytics");
		emitter.off("filter");
	}

	initDate() {
		let dateRange = {
			currentDataDateFrom: moment().subtract(7, "d").format("YYYY-MM-DD"),
			currentDataDateTo: moment().format("YYYY-MM-DD"),
		};
		sessionStorage.setItem("dateRange", JSON.stringify(dateRange));

		this.setState({
			data: {
				...this.state.data,
				...{ dateRange },
			},
		});
	}

	initFilters() {
		const filters = {
			mediaType: [],
			brand: [],
			mediaSource: [],
			category: [],
			subCategory: [],
			type: [],
			sentiment: [],
			author: [],
			topic: [],
			advertType: [],
			language: [],
			country: [],
			location: [],
			exclude: [],
			search: "",
		};

		sessionStorage.setItem("filters", JSON.stringify(filters));
		sessionStorage.setItem("editorialChecked", true);
		sessionStorage.setItem("advertisingChecked", true);
		sessionStorage.setItem("adspendChecked", false);
	}

	initPressedMediaTypes() {
		sessionStorage.setItem("pressedMediaTypes", JSON.stringify([]));
	}

	getClient() {
		return JSON.parse(sessionStorage.getItem("selectedClient"));
	}

	setClient(client) {
		sessionStorage.setItem("selectedClient", JSON.stringify(client));
	}

	getData() {
		emitter.emit("loading", true);
		this.selectedClient = this.getClient();
		const currentUserId = AuthService.getCurrentUser().userId;
		const { userClients } = this.props;
		const hasClientsForUser = userClients.userId === currentUserId && Array.isArray(userClients.clients);

		if (hasClientsForUser) {
			this.getDataWithClients(userClients.clients);
			return;
		}

		this.props.fetchUserClients(currentUserId)
			.unwrap()
			.then(() => {
				const { userClients: uc } = store.getState();
				this.getDataWithClients(uc.clients);
			})
			.catch((err) => {
				console.error("GET DATA ERROR", err);
				emitter.emit("loading", false);
			});
	}

	getDataWithClients(activeClients) {
		if (!activeClients || activeClients.length === 0) {
			emitter.emit("loading", false);
			return;
		}

		this.setState({
			data: {
				...this.state.data,
				clients: activeClients,
			},
		}, () => {
			if (!this.selectedClient) {
				this.setClient(activeClients[0].client);
				this.selectedClient = this.getClient();
			}

			if (!sessionStorage.getItem("filters")) {
				this.initDate();
				this.initFilters();
				this.initPressedMediaTypes();
			}

			apiData
				.getClient(
						this.selectedClient.id,
						AuthService.getCurrentUser().userId,
						true
					)
					.then((response) => {
						const aClientConfigs = [];

						response[0].clientConfigs.forEach((d) => {
							const aTypes = [];
							const aCountries = [];
							const aMediaTypes = [];
							const aBrands = [];
							const aCategories = [];
							const aSubCategories = [];

							d.clientConfigBrands.forEach((m) => {
								if (!aBrands.includes(m.brand.brandName)) {
									aBrands.push(m.brand.brandName);
								}
							});

							d.clientConfigCategories.forEach((m) => {
								if (
									!aCategories.includes(
										m.category.categoryName
									)
								) {
									aCategories.push(m.category.categoryName);
								}
							});

							d.clientConfigCountries.forEach((m) => {
								if (
									!aCountries.includes(m.country.countryName)
								) {
									aCountries.push(m.country.countryName);
								}
							});

							d.clientConfigMediatypes.forEach((m) => {
								if (
									!aMediaTypes.includes(
										m.mediatype.mediatypeName
									)
								) {
									aMediaTypes.push(m.mediatype.mediatypeName);
								}
							});

							d.clientConfigSubcategories.forEach((m) => {
	
								if ( m.subCategory.subcategoryName !== null &&
									!aSubCategories.includes(
										m.subCategory.subcategoryName
									)
								) {
									aSubCategories.push(
										m.subCategory.subcategoryName.toUpperCase()
									);
								}
							});

							d.clientConfigTypes.forEach((m) => {
								if (!aTypes.includes(m.type.typeName)) {
									aTypes.push(m.type.typeName);
								}
							});

							aClientConfigs.push({
								brands: aBrands,
								categories: aCategories,
								countries: aCountries,
								mediatypes: aMediaTypes,
								subcategories: aSubCategories,
								types: aTypes,
							});
						});

						const daysForPreviousData = moment
							.duration(
								moment(
									this.state.data.dateRange.currentDataDateTo
								).diff(
									moment(
										this.state.data.dateRange
											.currentDataDateFrom
									)
								)
							)
							.asDays();
						let dateForPreviousData = moment(
							this.state.data.dateRange.currentDataDateFrom
						)
							.subtract(daysForPreviousData + 1, "days")
							.format("YYYY-MM-DD");
						const dateForPreviousDataEnd = moment(
							this.state.data.dateRange.currentDataDateFrom
						)
							.subtract(1, "days")
							.format("YYYY-MM-DD");

						let dataFilter = JSON.parse(
							sessionStorage.getItem("filters")
						);
						// Resolve indices to names so API always gets string values (fixes Windows vs server payload difference)
						dataFilter = resolveFilterIndicesToNames(dataFilter, this.state.data);

						const currClient = JSON.parse(
							sessionStorage.getItem("selectedClient")
						);

						dataFilter.clientId = currClient.id;
						dataFilter.userId = AuthService.getCurrentUser().userId;

						dataFilter.dateFrom =
							this.state.data.dateRange.currentDataDateFrom;
						dataFilter.dateTo =
							this.state.data.dateRange.currentDataDateTo;

						dataFilter.config = aClientConfigs;
						sessionStorage.setItem(
							"dataFilter",
							JSON.stringify(dataFilter)
						);

						// Send filter object as JSON body (do not stringify – axios serializes the object)
						apiData
							.getAnalytics(dataFilter)
							.then((response) => {

								if (response?.dashboardAnalytics?.sentimentDistribution) {
									response.dashboardAnalytics.sentimentDistribution.forEach(item => {
										item.sentiment = item.sentiment.replace("*", "");	
									});
								}													
													
								const types = [];
								const arrMediaType =
									response.graphFilters.types;
								arrMediaType.map((x) => types.push(x));
								if (!types.includes("ADSPEND")) types.push("ADSPEND");

								const countries = [];
								const arrMediaCountry =
									response.graphFilters.countries;
								arrMediaCountry.map((x) => countries.push(x));

								const mediatypes = [];
								const arrMediaMediaTypes =
									response.graphFilters.mediatypes;
								arrMediaMediaTypes.map((x) =>
									mediatypes.push(x)
								);

								const brands = [];
								const arrMediaBrands =
									response.graphFilters.brands;
								arrMediaBrands.map((x) => brands.push(x));

								const categories = [];
								const arrMediaCategory =
									response.graphFilters.categories;
								arrMediaCategory.map((x) => categories.push(x));

								const subcategories = [];
								const arrMediaSubCategory =
									response.graphFilters.subcategories;
								arrMediaSubCategory.map((x) =>
									subcategories.push(x)
								);

								const topics = [];
								const arrMediaTopics =
									response.graphFilters.topics;
								arrMediaTopics.map((x) => topics.push(x));

								const lms = [];
								const arrMediaSource =
									response.graphFilters.sources;
								arrMediaSource.map((x) =>
									lms.push({ id: x, itemName: x })
								);

								const lang = [];
								const arrLanguage =
									response.graphFilters.languages;
								arrLanguage.map((x) =>
									lang.push({ id: x, itemName: x })
								);

								const adtype = [];
								const arrAdtypes =
									response.graphFilters.advertisingtypes;
								arrAdtypes.map((x) =>
									adtype.push({ id: x, itemName: x })
								);

								const loc = [];
								const arrLocation =
									response.graphFilters.regions;
								arrLocation.map((x) =>
									loc.push({ id: x, itemName: x })
								);

								const sent = [];
								const arrSentiment =
									response.graphFilters.sentiments;
								arrSentiment.map((x) =>
									sent.push({ id: x, itemName: x })
								);

								const auth = [];
								const arrAuthor = response.graphFilters.authors;
								arrAuthor.map((x) =>
									auth.push({ id: x, itemName: x })
								);

								emitter.emit("applysubscription");
								emitter.emit("applylistsubscription");

								this.setState(
									{
										data: {
											...this.state.data,
											totalRows: response.totalRows,
											sourceDistribution:
												response.dashboardAnalytics
													.sourceDistribution,
											authorDistribution:
												response.dashboardAnalytics
													.authorDistribution,
											sentimentDistribution:
												response.dashboardAnalytics
													.sentimentDistribution,
											mediatypeDistribution:
												response.dashboardAnalytics
													.mediatypeDistribution,
											languageDistribution:
												response.dashboardAnalytics
													.languageDistribution,
											regionDistribution:
												response.dashboardAnalytics
													.regionDistribution,
											countryDistribution:
												response.dashboardAnalytics
													.countryDistribution,
											growth: response.dashboardAnalytics
												.growth,
											engagement:
												response.dashboardAnalytics
													.engagement,
											topTweets:
												response.dashboardAnalytics
													.topTweets,

											zoomSourceDistribution:
												response.zoomAnalytics
													.sourceDistribution,
											zoomRegionDistribution:
												response.zoomAnalytics
													.regionDistribution,
											zoomCategoryDistribution:
												response.zoomAnalytics
													.categoryDistribution,
											zoomLanguageDistribution:
												response.zoomAnalytics
													.languageDistribution,
											zoomBrandDistribution:
												response.zoomAnalytics
													.brandDistribution,
											zoomTopicDistribution:
												response.zoomAnalytics
													.topicDistribution,		
											zoomAdvertTypeDistribution:
												response.zoomAnalytics
													.advertTypeDistribution,
											zoomLSMDistribution:
												response.zoomAnalytics
													.lsmDistribution,
											zoomAuthorDistribution:
												response.zoomAnalytics
													.authorDistribution,
											zoomRateCardDistribution:
												response.zoomAnalytics
													.rateCardDistribution,

											dynamics:
												response.dashboardAnalytics
													.dynamics,
											mediaTypeCards:
												response.dashboardAnalytics
													.mediaTypeCards,
											mediaCampaign:
												response.dashboardAnalytics
													.mediaCampaign,

											volumeData:
												response.graphAnalytics
													.volumeGraphData,
											mediaTypeData:
												response.graphAnalytics
													.mediaTypeGraphData,
											sourceData:
												response.graphAnalytics
													.sourceGraphData,
											authorData:
												response.graphAnalytics
													.authorGraphData,
											sentimentData:
												response.graphAnalytics
													.sentimentGraphData,
											languageData:
												response.graphAnalytics
													.languageGraphData,
											ratecardData:
												response.graphAnalytics
													.ratecardGraphData,
											regionData:
												response.graphAnalytics
													.regionGraphData,
											categoryData:
												response.graphAnalytics
													.categoryGraphData,
											lsmData:
												response.graphAnalytics
													.lsmGraphData,
											brandData:
												response.graphAnalytics
													.brandGraphData,
											topicData:
												response.graphAnalytics
													.topicGraphData,													
											advertTypeData:
												response.graphAnalytics
													.advertTypeGraphData,
											wordcloudData:
												response.graphAnalytics
													.wordCloudGraphData,

											types: types,
											countries: countries,
											mediaTypes: mediatypes,
											brands: brands,
											categories: categories,
											subcategories: subcategories,
											topics: topics,
											mediaSources: lms,
											languages: lang,
											advertTypes: adtype,
											locations: loc,
											sentiment: sent,
											authors: auth,

											mediaTypeSource: response.mediaTypeSource,

											dateRange: {
												...this.state.data.dateRange,
												...{
													previousDataDateFrom:
														dateForPreviousData,
													previousDataDateTo:
														dateForPreviousDataEnd,
												},
											},
										},
									},
									() => {
										emitter.emit("loading", false);
									}
								);
							})
							.catch((err) => {
								console.error("GET DATA ERROR", err);
								emitter.emit("loading", false);
							});
					})
					.catch((err) => {
						console.error("GET DATA ERROR", err);
						emitter.emit("loading", false);
					});
		});
	}

	render() {
		return (
			<Router>
				<Routes>
					{this.state.auth !== null ? (
						this.state.auth.logged === false ? (
							<>
								<Route path="/login" element={
									<React.Suspense fallback={<Loader />}>
										<Login />
									</React.Suspense>
								} />
								<Route
									path="/newsletter/view"
									element={
										<React.Suspense fallback={<Loader />}>
											<PublicNewsletter />
										</React.Suspense>
									}
								/>
								<Route path="*" element={<Navigate to="/login" replace />} />
							</>
						) : (
							<>
								<Route path="/" element={<Navigate to="/overview" replace />} />
								<Route
									path="/overview"
									element={
										<LayoutRoute
											component={Dashboard}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/volume"
									element={
										<LayoutRoute
											component={Volume}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/mediaType"
									element={
										<LayoutRoute
											component={MediaType}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/source"
									element={
										<LayoutRoute
											component={Source}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/category"
									element={
										<LayoutRoute
											component={Category}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/language"
									element={
										<LayoutRoute
											component={Language}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/lsmsem"
									element={
										<LayoutRoute
											component={LSMSEM}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/value"
									element={
										<LayoutRoute
											component={Value}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/brand"
									element={
										<LayoutRoute
											component={Brand}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/topic"
									element={
										<LayoutRoute
											component={Topic}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/advertType"
									element={
										<LayoutRoute
											component={AdvertType}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/topAuthors"
									element={
										<LayoutRoute
											component={TopAuthors}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/alertHistory"
									element={
										<LayoutRoute
											component={AlertHistory}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/faq"
									element={
										<LayoutRoute
											component={FAQ}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/sentiment"
									element={
										<LayoutRoute
											component={Sentiment}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/data"
									element={
										<LayoutRoute
											component={Data}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/sandbox"
									element={
										<LayoutRoute
											component={Sandbox}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/region"
									element={
										<LayoutRoute
											component={Region}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/wordCloud"
									element={
										<LayoutRoute
											component={WordCloud}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/userManagement"
									element={
										<LayoutRoute
											component={UserManagement}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/clientManagement"
									element={
										<LayoutRoute
											component={ClientManagement}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/socialMediaEngagement"
									element={
										<LayoutRoute
											component={SocialMediaEngagement}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/newsletters"
									element={
										<LayoutRoute
											component={Newsletters}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/newsletters/:id"
									element={
										<LayoutRoute
											component={NewsletterEditor}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/ebooks"
									element={
										<LayoutRoute
											component={Ebooks}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/ebooks/:id"
									element={
										<LayoutRoute
											component={EbookEditor}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/ebook/view/:id"
									element={
										<LayoutRoute
											component={EbookView}
											data={this.state.data}
											user={this.state.auth}
										/>
									}
								/>
								<Route
									path="/newsletter/view"
									element={
										<React.Suspense fallback={<Loader />}>
											<PublicNewsletter />
										</React.Suspense>
									}
								/>
								<Route path="*" element={<Navigate to="/overview" replace />} />
							</>
						)
					) : (
						<>
							<Route
								path="/newsletter/view"
								element={
									<React.Suspense fallback={<Loader />}>
										<PublicNewsletter />
									</React.Suspense>
								}
							/>
							<Route path="*" element={
								<React.Suspense fallback={<Loader />}>
									<Login />
								</React.Suspense>
							} />
						</>
					)}
				</Routes>
			</Router>
		);
	}
}

const mapStateToProps = (state) => ({ userClients: state.userClients });
const mapDispatchToProps = { getUserSandbox, fetchUserClients };
const ReduxAppRouter = connect(mapStateToProps, mapDispatchToProps)(AppRouter);

function LayoutRoute({ component: Component, data, user }) {
	return (
		<React.Suspense fallback={<Loader />}>
			<Layout data={data} user={user}>
				<Component data={data} user={user} />
			</Layout>
		</React.Suspense>
	);
}

function Loader() {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				height: "100%",
				zIndex: 1505,
				position: "absolute",
				top: 0,
				left: 0,
				backgroundColor: "rgba(0,0,0,0.3)",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<img
				className={"loader"}
				src={require("./assets/img/logo-02.png")}
				alt="logo"
				style={{ width: 50, height: 50 }}
			/>
			<div
				style={{
					fontSize: 15,
					fontFamily: "Avenir",
					color: "#000",
					marginTop: 20,
				}}
			>
				Intelligence Loading
			</div>
		</div>
	);
}

export default function App() {
	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		emitter.on("loading", (value) => {
			setLoading(value);
		});
		return () => {
			emitter.off("loading");
		};
	});

	return (
		<>
			<ReduxAppRouter />
			{loading && <Loader />}
			<Toaster />
		</>
	);
}
